import { brand } from '../lib/i18n.ts';

/**
 * شرایط استفاده و سیاست حریم خصوصی {brand}.
 *
 * چرا این‌جا و نه توی i18n: این متن سند است، نه برچسبِ رابط. باید نسخه
 * داشته باشد، باید بشود گفت کاربر «کدام نسخه» را پذیرفته، و نباید با
 * ترجمه‌ی ماشینیِ سیزده‌زبانه رقیق شود. متنِ حقوقی را فقط به زبانی
 * می‌نویسیم که بتوان مسئولیتش را پذیرفت — فارسی و انگلیسی.
 *
 * فایل عمداً هیچ چیزی از react-native وارد نمی‌کند تا `npm test` بتواند
 * بخواندش.
 *
 * ⚠️ این متن توسط وکیل بازبینی نشده. پیش از عرضه‌ی عمومی باید بشود —
 * به‌ویژه بند محدودیت مسئولیت (بند ۱۸) که در برخی حوزه‌های قضایی نافذ
 * نیست، و بند ۲۲ (قانون حاکم) که آن محدودیت به آن تکیه دارد. این یک
 * آیتمِ **حقوقی** است نه کدنویسی؛ WHOSUP-170 آن را به‌عمد باز گذاشت تا
 * فراموش نشود.
 *
 * ⚠️ و یک قاعده‌ی ساده که همان تیکت از یک تناقضِ واقعی بیرون کشید: هیچ
 * جمله‌ی **مطلقی** در این سند ننویسید که کد بتواند نقضش کند. «هیچ پیامکی
 * ارسال نمی‌شود» یک سال درست بود و بعد با آمدنِ تاییدِ شماره (Twilio،
 * supabase/functions/send-sms) غلط شد، بی آنکه چیزی خطا بدهد. اگر ادعایی
 * لازم است، دامنه‌اش را همان‌جا بنویسید («فقط برای …، و فقط وقتی …»).
 *
 * ⚠️ حداقلِ سنِ بند ۳ عددِ دلخواه نیست و پایین آوردنش تصمیمِ کوچکی نیست:
 * روی ۱۸ است تا با Target audienceِ Play Console بخواند و اپ مشمولِ
 * Families policy نشود (WHOSUP-170). عوض کردنش این‌جا یعنی عوض کردنِ آن
 * تنظیم در کنسول هم — وگرنه همان ناهماهنگیِ سند/فروشگاه که این تیکت
 * بست، از سرِ دیگرش باز می‌شود.
 */

/**
 * با هر تغییر معنادار در متن، این عدد بالا می‌رود و کاربر دوباره می‌پذیرد.
 *
 * «دوباره می‌پذیرد» یک آرزو نیست، یک گیت است: `needsTermsAcceptance` پایینِ
 * همین فایل، و صفحه‌ای که هر دو اپ پیش از هر چیزِ دیگر نشان می‌دهند.
 */
export const TERMS_VERSION = 3;
export const TERMS_DATE = '۱۴۰۵/۰۷/۱۵';
export const TERMS_DATE_EN = '7 October 2026';
export const CONTACT_EMAIL = 'privacy@outami.com';

export type LegalSection = { n: number; title: string; body: string[] };
export type LegalDoc = {
  title: string;
  updated: string;
  intro: string;
  sections: LegalSection[];
  close: string;
};

const FA_SECTIONS: LegalSection[] = [
  {
    n: 1,
    title: 'پذیرش شرایط',
    body: [
      'با علامت‌گذاری گزینه‌ی پذیرش در هنگام ثبت‌نام، یا با هرگونه استفاده از خدمات {brand} («خدمات»)، شما اعلام می‌کنید که مفاد این سند را به‌طور کامل خوانده، درک کرده و پذیرفته‌اید و خود را ملزم به رعایت آن می‌دانید.',
      'چنانچه با هر بخش از این شرایط موافق نیستید، از خدمات استفاده نکنید. علامت‌گذاری گزینه‌ی پذیرش شرطِ لازم برای ایجاد حساب کاربری است و بدون آن ثبت‌نام انجام نخواهد شد.',
    ],
  },
  {
    n: 2,
    title: 'تعاریف',
    body: [
      '«ارائه‌دهنده» به گرداننده‌ی خدمات {brand} اطلاق می‌شود.',
      '«کاربر» یا «شما» به شخص حقیقی دارنده‌ی حساب کاربری اطلاق می‌شود.',
      '«سیگنال» به دعوت یا پیشنهادی اطلاق می‌شود که کاربر برای گردهمایی یا فعالیتی ایجاد می‌کند.',
      '«دفترچه‌ی مخاطبین» به فهرست مخاطبین ذخیره‌شده در دستگاه کاربر اطلاق می‌شود.',
      '«دستگاه» به تلفن همراه یا هر سخت‌افزاری اطلاق می‌شود که خدمات بر روی آن اجرا می‌گردد.',
    ],
  },
  {
    n: 3,
    title: 'شرایط احراز صلاحیت',
    body: [
      'استفاده از خدمات منحصراً برای اشخاصی مجاز است که حداقل هجده (۱۸) سال تمام داشته باشند. با پذیرش این شرایط، شما تصریح می‌کنید که واجد این شرط سنی هستید.',
      'چنانچه ارائه‌دهنده به هر نحو مطلع شود که حسابی توسط شخصی زیر سن مقرر ایجاد شده است، آن حساب و داده‌های مرتبط با آن بدون اطلاع قبلی حذف خواهد شد.',
      'شما متعهد می‌شوید اطلاعاتی که در هنگام ثبت‌نام ارائه می‌کنید صحیح، کامل و به‌روز باشد و در صورت تغییر، آن را اصلاح نمایید.',
    ],
  },
  {
    n: 4,
    title: 'حساب کاربری و مسئولیت حفاظت از آن',
    body: [
      'مسئولیت حفظ محرمانگی گذرواژه و کلیه‌ی فعالیت‌هایی که از طریق حساب شما انجام می‌شود، منحصراً بر عهده‌ی شماست.',
      'نشانی رایانامه‌ی شما به‌صورت خام و رمزنگاری‌نشده نگهداری می‌شود، زیرا برای ورود، بازیابی گذرواژه و ارسال اطلاعیه‌های ضروری خدمات لازم است.',
      'شماره‌ی تلفن همراه شما، در صورت ارائه، به‌صورت خام نگهداری می‌شود، زیرا برای احراز هویت، تأیید مالکیت شماره و امکان یافته‌شدن شما توسط آشنایانتان ضرورت دارد.',
      'هر شماره‌ی تلفن در هر زمان تنها به یک حساب تعلق می‌گیرد. انتقال یک شماره به حساب دیگر مستلزم گذشت یک دوره‌ی انتظار سه‌روزه است تا از تصاحب شماره‌ی دیگران جلوگیری شود.',
      'در صورت اطلاع از هرگونه استفاده‌ی غیرمجاز از حسابتان، موظفید بی‌درنگ مراتب را به نشانی مندرج در بند ۲۴ اطلاع دهید.',
    ],
  },
  {
    n: 5,
    title: 'دسترسی به دفترچه‌ی مخاطبین',
    body: [
      'خدمات، تنها پس از اخذ رضایت صریح شما و صدور مجوز از سوی سیستم‌عامل دستگاه، به دفترچه‌ی مخاطبین شما دسترسی می‌یابد. این رضایت در هر زمان از طریق تنظیمات دستگاه قابل بازپس‌گیری است.',
      'دفترچه‌ی مخاطبین شما بر روی دستگاه شما باقی می‌ماند. ارائه‌دهنده نسخه‌ای از فهرست مخاطبین شما را بر روی سرورهای خود نگهداری نمی‌کند.',
      'برای یافتنِ آشنایانی که پیش‌تر عضو شده‌اند، برنامه شماره‌ها و نشانی‌های رایانامه‌ی دفترچه‌ی شما را بر روی خودِ دستگاه به مقادیر درهم‌سازی‌شده (hash) تبدیل می‌کند و تنها همان مقادیر را برای تطبیق ارسال می‌نماید. این مقادیر تنها در همان لحظه پردازش می‌شوند، در پاسخ به همان درخواست، و بر روی سرور نگهداری نمی‌شوند.',
      'مخاطبینی که شما آنان را به‌عنوان دوست علامت‌گذاری کرده‌اید، یا به‌صورت دستی ایجاد نموده‌اید، یا به یکی از گروه‌های خود افزوده‌اید، از این قاعده مستثنا هستند. اطلاعات این دسته از مخاطبین — شامل نام، شماره‌ی تلفن و نشانی رایانامه — بر روی سرور نگهداری می‌شود، زیرا این اطلاعات بخشی از روابط شما در خدمات است و باید میان دستگاه‌های مختلف شما پایدار بماند.',
      'مخاطبینی که در هیچ‌یک از دسته‌های فوق قرار نمی‌گیرند، بر روی سرور ذخیره نمی‌شوند.',
      'ارائه‌دهنده دفترچه‌ی مخاطبین شما را به هیچ شخص ثالثی نمی‌فروشد، اجاره نمی‌دهد و در اختیار نمی‌گذارد، و از آن برای مقاصد تبلیغاتی استفاده نمی‌کند.',
      'داده‌های مخاطبینی که پیش‌تر بر روی سرور ذخیره شده و مشمول استثنای فوق نبوده‌اند، حذف شده‌اند.',
    ],
  },
  {
    n: 6,
    title: 'اطلاعاتی که گردآوری می‌شود',
    body: [
      'اطلاعات حساب: نشانی رایانامه، گذرواژه‌ی درهم‌سازی‌شده، شماره‌ی تلفن (اختیاری).',
      'اطلاعات نمایه: نام نمایشی، نام کاربری، شهر، کشور، تصویر نمایه و متن وضعیت.',
      'محتوای تولیدشده توسط شما: سیگنال‌ها، پیام‌های گفتگو، گروه‌ها، برچسب‌ها، دسته‌بندی‌های شخصی، موارد نشان‌شده و بازخوردهایی که ارسال می‌کنید.',
      'اطلاعات فنی: نشانه‌ی اعلان (push token)، نوع سیستم‌عامل، نسخه‌ی نصب‌شده‌ی برنامه، و گزارش خطاهای رخ‌داده جهت رفع اشکال.',
      'موقعیت مکانی دستگاه: تنها با اجازه‌ی صریح شما و تنها هنگامی که خودتان گزینه‌ی «نزدیک‌ترین» یا «شهرم را پیدا کن» را انتخاب کنید. از آن برای مرتب‌سازی فهرست بر پایه‌ی فاصله و برای یافتن شهر شما استفاده می‌شود. مختصات جغرافیایی شما ذخیره نمی‌شود؛ تنها شهرِ به‌دست‌آمده در نمایه‌ی شما می‌نشیند.',
      'ثبتِ رفتار شما در خدمات: اینکه کدام رخداد را باز کرده‌اید و بر روی لینکِ کدام رخداد کلیک کرده‌اید، و تاریخچه‌ی تغییرِ ترجیحات شما (مانند زبان، پوسته و شهر). این داده‌ها برای هیچ کاربرِ دیگری قابل خواندن نیست و تنها در اختیار خودِ شما و ارائه‌دهنده است.',
      'استفاده‌ی بدونِ حساب (مهمان): صفحه‌ی کشف بدونِ ثبت‌نام هم در دسترس است. رفتارِ مهمان در خدمات (صفحه‌هایی که باز می‌شود و دکمه‌هایی که زده می‌شود) با یک شناسه‌ی تصادفی ثبت می‌شود که خودِ برنامه یک بار می‌سازد و بر روی همان دستگاه نگه می‌دارد. این شناسه از سخت‌افزار یا شناسه‌ی تبلیغاتیِ دستگاه گرفته نمی‌شود و بیرون از خدمات معنایی ندارد. اگر بعداً ثبت‌نام یا وارد شوید، این سابقه به حسابِ شما پیوند می‌خورد. پاک کردنِ برنامه یا داده‌های مرورگر این شناسه را از بین می‌برد.',
      'ارائه‌دهنده از ابزارهای ردیابی تبلیغاتی، شناسه‌های تبلیغاتی و تحلیل‌گرهای شخص ثالث استفاده نمی‌کند.',
    ],
  },
  {
    n: 7,
    title: 'اهداف پردازش اطلاعات',
    body: [
      'ارائه‌ی خدمات و امکان‌پذیر ساختن کارکردهای اصلی آن، از جمله ایجاد سیگنال، دعوت دوستان و گفتگو.',
      'احراز هویت، حفظ امنیت حساب و پیشگیری از سوءاستفاده.',
      'ارسال اعلان‌های مرتبط با فعالیت شما و دوستانتان.',
      'شناسایی و رفع اشکالات فنی و بهبود پایداری خدمات.',
      'ارائه‌دهنده اطلاعات شما را برای تصمیم‌گیری خودکاری که آثار حقوقی برای شما داشته باشد، پردازش نمی‌کند.',
    ],
  },
  {
    n: 8,
    title: 'افشا به اشخاص ثالث',
    body: [
      'ارائه‌ی خدمات متکی بر زیرساخت‌های زیر است و اطلاعات شما، در حدود لازم برای عملکرد آن‌ها، بر روی این زیرساخت‌ها پردازش می‌شود:',
      'Supabase — پایگاه داده، احراز هویت و ذخیره‌سازی فایل.',
      'Expo — انتشار به‌روزرسانی برنامه و واسطه‌ی ارسال اعلان.',
      'Firebase Cloud Messaging — تحویل اعلان بر روی دستگاه‌های اندروید.',
      'Apple Push Notification service — تحویل اعلان بر روی دستگاه‌های iOS.',
      'Twilio — ارسال پیامکِ تأیید شماره. شماره‌ی تلفن شما، در حدی که برای تحویل همان پیامک لازم است، در اختیار این سرویس قرار می‌گیرد.',
      'Apple و Google — تنها در صورتی که «ورود با اپل» یا «ورود با گوگل» را انتخاب کنید. در آن حالت، احراز هویت نزد آن سرویس انجام می‌شود و شناسه‌ی حساب و نشانی رایانامه‌ای که خود اجازه‌ی به‌اشتراک‌گذاری‌اش را می‌دهید به خدمات بازمی‌گردد.',
      'Cloudflare Pages — میزبانی نسخه‌ی وبِ خدمات. درخواست‌های مرورگر شما از شبکه‌ی این سرویس عبور می‌کند و در نتیجه نشانی شبکه‌ی (IP) شما برای آن قابل مشاهده است.',
      'OpenStreetMap — هنگامی که نمای نقشه را باز می‌کنید، درخواست دریافت تصاویر نقشه از سرورهای این سرویس ارسال می‌شود و در نتیجه نشانی شبکه‌ی (IP) شما برای آن سرویس قابل مشاهده خواهد بود.',
      'ارائه‌دهنده اطلاعات شخصی شما را نمی‌فروشد و در ازای دریافت وجه در اختیار دیگران قرار نمی‌دهد.',
      'ارائه‌دهنده ممکن است اطلاعات را در پاسخ به دستور مقام قضایی صالح، یا در مواردی که برای حفاظت از حقوق، ایمنی یا اموال اشخاص ضرورت داشته باشد، افشا نماید.',
    ],
  },
  {
    n: 9,
    title: 'محتوای کاربر',
    body: [
      'کلیه‌ی حقوق مالکیت محتوایی که شما ایجاد می‌کنید متعلق به خود شماست.',
      'شما با ایجاد محتوا، به ارائه‌دهنده مجوزی غیرانحصاری، بدون حق‌الامتیاز و محدود اعطا می‌کنید تا صرفاً به‌منظور ارائه‌ی خدمات، آن محتوا را ذخیره، تکثیر و برای مخاطبانی که خود تعیین کرده‌اید نمایش دهد. این مجوز با حذف محتوا یا حساب شما خاتمه می‌یابد.',
      'مسئولیت محتوایی که منتشر می‌کنید، از جمله رعایت حقوق اشخاص ثالث، منحصراً بر عهده‌ی شماست.',
    ],
  },
  {
    n: 10,
    title: 'رفتارهای ممنوع',
    body: [
      'آزار، تهدید، ارعاب یا انتشار محتوای مستهجن، تبعیض‌آمیز یا خشونت‌آمیز.',
      'جعل هویت اشخاص حقیقی یا حقوقی، یا ارائه‌ی اطلاعات نادرست درباره‌ی هویت خود.',
      'برداشت انبوه، خزش خودکار یا استخراج نظام‌مند اطلاعات کاربران به هر وسیله.',
      'تلاش برای دسترسی غیرمجاز به حساب دیگران، سرورها یا بخش‌های محافظت‌شده‌ی خدمات.',
      'استفاده‌ی تجاری، تبلیغاتی یا ارسال پیام انبوه ناخواسته.',
      'ارائه‌دهنده مجاز است در صورت احراز هر یک از موارد فوق، حساب کاربر را بدون اطلاع قبلی تعلیق یا حذف نماید.',
      'تحمل صفر: خدمات هیچ مدارایی با محتوای ناپسند یا کاربرِ آزارگر ندارد. محتوای ناقضِ این بند حذف و کاربرِ ناقض از خدمات اخراج می‌شود.',
      'هر کاربر می‌تواند هر سیگنال، پیام یا نمایه‌ای را از درون برنامه گزارش کند و هر کاربری را مسدود نماید؛ محتوای کاربرِ مسدودشده بی‌درنگ از دید شما پنهان می‌شود. ارائه‌دهنده گزارش‌ها را ظرف ۲۴ ساعت بررسی و در صورت احراز تخلف، محتوا را حذف و کاربر را اخراج می‌کند.',
      'متن سیگنال‌ها، پیام‌ها و نام نمایه پیش از انتشار با فهرستی از واژه‌های ناپسند سنجیده می‌شود و در صورت تطابق منتشر نمی‌شود. اگر گمان می‌کنید این سنجش به‌اشتباه جلوی متن شما را گرفته، می‌توانید از همان‌جا اعتراض کنید تا ارائه‌دهنده آن را بررسی کند.',
    ],
  },
  {
    n: 11,
    title: 'دیدارهای حضوری و سلب مسئولیت',
    body: [
      'خدمات صرفاً ابزاری برای هماهنگی میان اشخاصی است که یکدیگر را می‌شناسند. ارائه‌دهنده هویت، پیشینه، صلاحیت یا حسن نیت هیچ کاربری را بررسی، تأیید یا تضمین نمی‌کند.',
      'تصمیم به ملاقات حضوری با هر شخص، انتخاب مکان، زمان و شرایط آن، و کلیه‌ی پیامدهای ناشی از آن، منحصراً بر عهده‌ی خود شماست.',
      'ارائه‌دهنده در قبال هرگونه خسارت جانی، مالی یا معنوی که در جریان یا در نتیجه‌ی دیدار حضوری میان کاربران واقع شود، هیچ‌گونه مسئولیتی نمی‌پذیرد.',
      'رعایت احتیاط‌های متعارف، از جمله انتخاب مکان عمومی برای اولین دیدار، به کاربران توصیه می‌شود.',
    ],
  },
  {
    n: 12,
    title: 'اطلاعات مکان‌ها، رخدادها و کسب‌وکارها',
    body: [
      'اطلاعات مربوط به مکان‌ها، رخدادها و کسب‌وکارها که در بخش کاوش نمایش داده می‌شود، ممکن است از منابع عمومی گردآوری شده یا توسط کاربران ثبت شده باشد.',
      'ارائه‌دهنده صحت، به‌روز بودن یا کامل بودن این اطلاعات — از جمله ساعات کار، قیمت‌ها، نشانی و در دسترس بودن — را تضمین نمی‌کند.',
      'پیش از هرگونه اقدام مبتنی بر این اطلاعات، راستی‌آزمایی مستقیم آن بر عهده‌ی شماست.',
    ],
  },
  {
    n: 13,
    title: 'پیامک و ارتباط با اشخاص خارج از خدمات',
    body: [
      'خدمات تنها برای تأیید شماره‌ی تلفن پیامک ارسال می‌کند، و تنها هنگامی که خودِ شما این کار را آغاز کنید. آن پیامک حاوی یک کد یک‌بارمصرف است و تحویل آن از طریق سرویس Twilio انجام می‌شود (بند ۸). جز این مورد، خدمات پیامکی ارسال نمی‌کند.',
      'خدمات به پیام‌های ذخیره‌شده بر روی دستگاه شما دسترسی ندارد.',
      'دعوت از طریق پیامک از این قاعده جداست: هنگامی که این گزینه را انتخاب می‌کنید، برنامه‌ی پیام‌رسان خودِ دستگاه با متن از پیش آماده گشوده می‌شود و ارسال آن با اقدام مستقیم شما صورت می‌گیرد. در این مسیر نه ارائه‌دهنده و نه هیچ سرویس ثالثی نقشی ندارد و متن پیام از سرورهای خدمات عبور نمی‌کند.',
      'هزینه‌ی ارسال پیامک تابع تعرفه‌ی اپراتور شماست و ارائه‌دهنده در آن دخالتی ندارد.',
    ],
  },
  {
    n: 14,
    title: 'اعلان‌ها',
    body: [
      'خدمات ممکن است اعلان‌هایی درباره‌ی فعالیت دوستان، دعوت‌ها و پیام‌ها ارسال کند.',
      'شما می‌توانید در هر زمان اعلان‌ها را از طریق تنظیمات برنامه یا تنظیمات دستگاه غیرفعال کنید.',
    ],
  },
  {
    n: 15,
    title: 'حقوق شما نسبت به داده‌ها',
    body: [
      'دسترسی به داده‌هایی که درباره‌ی شما نگهداری می‌شود.',
      'اصلاح داده‌های نادرست یا ناقص.',
      'حذف حساب و داده‌های مرتبط با آن.',
      'حذف بخشی از داده‌های خود بدون حذف حساب: تصویر نمایه، شماره‌های تلفن، متن وضعیت، مخاطبینی که از دستگاه خود وارد کرده‌اید، سیگنال‌هایی که ساخته‌اید، رخدادهایی که افزوده‌اید، و نام شما بر روی پیام‌هایی که فرستاده‌اید. هر یک جداگانه و بی‌درنگ از همان صفحه‌ی حذف در تنظیمات انجام می‌شود و نیازی به درخواست از ارائه‌دهنده ندارد.',
      'سه مورد آخر را دیگران نیز دیده‌اند و از این رو حدی دارند: سیگنالی که دوستانتان پیش‌تر به آن پاسخ داده‌اند و رخدادی که کسی بر پایه‌ی آن برنامه‌ای ساخته است باقی می‌مانند و «لغوشده» نشان داده می‌شوند، تا آنچه دیگران بر آن بنا کرده‌اند بی‌معنا نشود؛ و متن پیام‌های شما در گفتگوی دیگران می‌ماند، لیکن ارتباط آن با نام و نمایه‌ی شما قطع می‌گردد.',
      'بازپس‌گیری رضایت‌های اعطاشده، از جمله دسترسی به دفترچه‌ی مخاطبین.',
      'اعمال هر یک از حقوق فوق از طریق بخش تنظیمات برنامه یا مکاتبه با نشانی مندرج در بند ۲۴ امکان‌پذیر است.',
    ],
  },
  {
    n: 16,
    title: 'نگهداری و حذف داده‌ها',
    body: [
      'داده‌های شما تا زمانی نگهداری می‌شود که حساب شما فعال باشد.',
      'با حذف حساب، نمایه، سیگنال‌ها، پیام‌ها، گروه‌ها و مخاطبین ذخیره‌شده‌ی شما حذف می‌شوند.',
      'همچنین نام نمایشی، نام کاربری، نشانی رایانامه و شماره‌ی تلفن شما درهم‌سازی (hash) می‌شوند، به‌گونه‌ای که بازگرداندن آن‌ها به مقدار اصلی ممکن نباشد. این کار بازگشت‌ناپذیر است و حسابِ حذف‌شده احیا نمی‌شود.',
      'پیام‌هایی که در گفتگوی سیگنال دیگران ارسال کرده‌اید ممکن است برای حفظ انسجام آن گفتگو باقی بماند، لیکن ارتباط آن با نمایه‌ی شما قطع می‌شود.',
      'نسخه‌های پشتیبان ممکن است تا مدت متعارفی پس از حذف، همچنان حاوی داده باشند و در چرخه‌ی عادی خود بازنویسی شوند.',
    ],
  },
  {
    n: 17,
    title: 'ارائه‌ی خدمت «همان‌گونه که هست»',
    body: [
      'خدمات «همان‌گونه که هست» و «با هر نقصی که دارد» عرضه می‌شود.',
      'ارائه‌دهنده هیچ‌گونه تضمین صریح یا ضمنی نسبت به تداوم بی‌وقفه، عاری از خطا بودن، امنیت مطلق یا تناسب خدمات با هدف خاصی از سوی کاربر ارائه نمی‌کند.',
      'ارائه‌دهنده تضمین نمی‌کند که سیگنال‌ها، پیام‌ها یا اعلان‌ها در زمان مقرر یا اساساً تحویل داده شوند.',
      'ارائه‌دهنده مجاز است در هر زمان و بدون اطلاع قبلی، هر بخش از خدمات را تغییر داده، محدود یا متوقف کند.',
    ],
  },
  {
    n: 18,
    title: 'محدودیت مسئولیت',
    body: [
      'تا حداکثری که قانون حاکم اجازه می‌دهد، ارائه‌دهنده در قبال خسارات غیرمستقیم، تبعی، اتفاقی، تنبیهی یا خاص — از جمله فوت منفعت، از دست رفتن داده یا لطمه به اعتبار — مسئولیتی نخواهد داشت.',
      'در هر حال، مجموع مسئولیت ارائه‌دهنده در برابر شما از مبلغی که ظرف دوازده ماه گذشته بابت خدمات پرداخت کرده‌اید تجاوز نخواهد کرد. چنانچه وجهی پرداخت نکرده باشید، این سقف صفر است.',
      'برخی حوزه‌های قضایی محدود کردن پاره‌ای از مسئولیت‌ها را مجاز نمی‌دانند؛ در چنین مواردی، محدودیت‌های فوق تنها تا حدی اعمال می‌شود که قانون اجازه دهد و سایر مفاد به قوت خود باقی است.',
    ],
  },
  {
    n: 19,
    title: 'جبران خسارت',
    body: [
      'شما متعهد می‌شوید ارائه‌دهنده را در برابر هرگونه دعوا، مطالبه، خسارت یا هزینه‌ی ناشی از نقض این شرایط توسط شما، محتوایی که منتشر کرده‌اید، یا نقض حقوق اشخاص ثالث از سوی شما، مصون نگاه دارید.',
    ],
  },
  {
    n: 20,
    title: 'تعلیق و خاتمه',
    body: [
      'شما در هر زمان می‌توانید با حذف حساب خود، به این رابطه‌ی قراردادی پایان دهید.',
      'ارائه‌دهنده می‌تواند در صورت نقض این شرایط، یا هنگامی که ادامه‌ی ارائه‌ی خدمت موجب مخاطره برای سایر کاربران باشد، دسترسی شما را تعلیق یا خاتمه دهد.',
      'مفاد بندهای ۹، ۱۱، ۱۸، ۱۹ و ۲۲ پس از خاتمه نیز به قوت خود باقی می‌ماند.',
    ],
  },
  {
    n: 21,
    title: 'تغییر در این سند',
    body: [
      'ارائه‌دهنده حق دارد این شرایط را در هر زمان بازنگری کند. متن جاری همواره از بخش تنظیمات برنامه در دسترس است.',
      'در صورت تغییر بااهمیت، مراتب پیش از اجرایی شدن، از طریق برنامه به اطلاع شما خواهد رسید و پذیرش مجدد شما درخواست می‌شود.',
      'ادامه‌ی استفاده از خدمات پس از اجرایی شدن تغییرات، به‌منزله‌ی پذیرش نسخه‌ی جدید است. چنانچه نسخه‌ی جدید را نمی‌پذیرید، راه‌حل شما توقف استفاده و حذف حساب است.',
      'هر نسخه شماره‌ی مستقل دارد و تاریخ و شماره‌ی نسخه‌ای که پذیرفته‌اید ثبت و نگهداری می‌شود.',
    ],
  },
  {
    n: 22,
    title: 'قانون حاکم و مرجع رسیدگی',
    body: [
      'این شرایط تابع قوانین استان انتاریو و قوانین فدرال قابل اعمال کشور کانادا است.',
      'رسیدگی به هرگونه اختلاف ناشی از این شرایط در صلاحیت انحصاری محاکم واقع در استان انتاریو خواهد بود.',
      'چنانچه قانون آمره‌ی محل اقامت شما حقوقی فراتر از این سند برای شما مقرر کرده باشد، آن حقوق محفوظ است.',
    ],
  },
  {
    n: 23,
    title: 'تفکیک‌پذیری و کلیت توافق',
    body: [
      'بی‌اعتباری هر یک از مواد این سند به اعتبار سایر مواد خللی وارد نمی‌کند و ماده‌ی بی‌اعتبار تا حد ممکن به نزدیک‌ترین معنای معتبر تفسیر می‌شود.',
      'این سند کل توافق میان شما و ارائه‌دهنده در خصوص خدمات را تشکیل می‌دهد و جایگزین هرگونه تفاهم پیشین است.',
      'عدم اعمال فوری هر یک از حقوق مندرج در این سند از سوی ارائه‌دهنده، به‌منزله‌ی صرف‌نظر از آن حق تلقی نمی‌شود.',
    ],
  },
  {
    n: 24,
    title: 'تماس',
    body: [
      `پرسش‌ها، درخواست‌های مربوط به داده‌ها و اعلام موارد نقض این شرایط را به نشانی زیر ارسال کنید: ${CONTACT_EMAIL}`,
    ],
  },
];

const EN_SECTIONS: LegalSection[] = [
  {
    n: 1,
    title: 'Acceptance of Terms',
    body: [
      'By ticking the acceptance box at registration, or by any use of the {brand} services (the "Services"), you represent that you have read, understood and accepted this document in full and agree to be bound by it.',
      'If you do not agree with any part of these terms, do not use the Services. Ticking the acceptance box is a precondition to creating an account; without it, registration will not proceed.',
    ],
  },
  {
    n: 2,
    title: 'Definitions',
    body: [
      '"Provider" means the operator of the {brand} Services.',
      '"User" or "you" means the natural person holding an account.',
      '"Signal" means an invitation or proposal a user creates for a gathering or activity.',
      '"Address book" means the contact list stored on the user\'s device.',
      '"Device" means the mobile phone or other hardware on which the Services run.',
    ],
  },
  {
    n: 3,
    title: 'Eligibility',
    body: [
      'Use of the Services is permitted solely to persons who are at least eighteen (18) years of age. By accepting these terms you represent that you meet this requirement.',
      'If the Provider becomes aware that an account was created by a person below the stated age, that account and its associated data will be deleted without prior notice.',
      'You undertake that the information you provide at registration is accurate, complete and current, and that you will correct it if it changes.',
    ],
  },
  {
    n: 4,
    title: 'Account and Custody of Credentials',
    body: [
      'You are solely responsible for maintaining the confidentiality of your password and for all activity conducted through your account.',
      'Your email address is stored in plain, unhashed form, because it is required for sign-in, password recovery and essential service notices.',
      'Your mobile number, if provided, is stored in plain form, because it is required for verification, for confirming ownership of the number, and to allow people who know you to find you.',
      'A phone number belongs to only one account at a time. Transferring a number to another account requires a three-day waiting period, to prevent appropriation of another person\'s number.',
      'If you become aware of any unauthorised use of your account, you must notify the address in section 24 without delay.',
    ],
  },
  {
    n: 5,
    title: 'Access to the Address Book',
    body: [
      'The Services access your address book only after your express consent and the granting of permission by your device operating system. That consent may be withdrawn at any time through device settings.',
      'Your address book remains on your device. The Provider does not retain a copy of your contact list on its servers.',
      'To find people you already know who have joined, the application converts the phone numbers and email addresses in your address book into hashed values on the device itself, and transmits only those hashed values for matching. Those values are processed at that moment only, in response to that same request, and are not retained on the server.',
      'Contacts you have marked as friends, created manually, or added to one of your groups are excepted from this rule. The details of such contacts — name, phone number and email address — are stored on the server, because they form part of your relationships within the Services and must persist across your devices.',
      'Contacts falling into none of the above categories are not stored on the server.',
      'The Provider does not sell, rent or otherwise make your address book available to any third party, and does not use it for advertising purposes.',
      'Contact data previously stored on the server that did not fall within the above exception has been deleted.',
    ],
  },
  {
    n: 6,
    title: 'Information Collected',
    body: [
      'Account data: email address, hashed password, phone number (optional).',
      'Profile data: display name, username, city, country, profile picture and status text.',
      'Content you create: signals, chat messages, groups, tags, personal categories, saved items and feedback you submit.',
      'Technical data: push notification token, operating system type, installed application version, and error reports for the purpose of diagnosis.',
      'Device location: only with your express permission and only when you yourself choose "Nearest" or "Find my city". It is used to sort listings by distance and to identify your city. Your coordinates are not stored; only the resulting city is recorded in your profile.',
      'A record of your behaviour within the Services: which events you have opened and which event links you have clicked, and the history of changes to your preferences (such as language, theme and city). This data is not readable by any other user and is available only to you and to the Provider.',
      'Use without an account (guest): the Discover page is available without signing up. A guest\'s behaviour within the Services (pages opened and buttons pressed) is recorded under a random identifier that the application creates once and keeps on that device. It is not derived from the device hardware or its advertising identifier and has no meaning outside the Services. If you later sign up or sign in, that history is linked to your account. Removing the application or clearing browser data destroys the identifier.',
      'The Provider does not use advertising trackers, advertising identifiers or third-party analytics.',
    ],
  },
  {
    n: 7,
    title: 'Purposes of Processing',
    body: [
      'Providing the Services and enabling their core functions, including creating signals, inviting friends and messaging.',
      'Authentication, account security and prevention of misuse.',
      'Sending notifications relating to your activity and that of your friends.',
      'Identifying and correcting technical faults and improving stability.',
      'The Provider does not process your data for automated decision-making producing legal effects concerning you.',
    ],
  },
  {
    n: 8,
    title: 'Disclosure to Third Parties',
    body: [
      'Delivery of the Services relies on the following infrastructure, on which your data is processed to the extent necessary for its operation:',
      'Supabase — database, authentication and file storage.',
      'Expo — application update distribution and notification relay.',
      'Firebase Cloud Messaging — notification delivery on Android devices.',
      'Apple Push Notification service — notification delivery on iOS devices.',
      'Twilio — delivery of the phone-number verification text message. Your phone number is made available to that service to the extent required to deliver that message.',
      'Apple and Google — only if you choose "Continue with Apple" or "Continue with Google". In that case authentication takes place with that service, and your account identifier together with the email address you yourself permit to be shared is returned to the Services.',
      'Cloudflare Pages — hosting of the web version of the Services. Your browser\'s requests pass through that service\'s network, and consequently your network (IP) address is visible to it.',
      'OpenStreetMap — when you open the map view, requests for map imagery are sent to that service\'s servers, and consequently your network (IP) address becomes visible to it.',
      'The Provider does not sell your personal information and does not make it available to others for payment.',
      'The Provider may disclose information in response to a lawful order of a competent authority, or where necessary to protect the rights, safety or property of any person.',
    ],
  },
  {
    n: 9,
    title: 'User Content',
    body: [
      'All ownership rights in content you create remain with you.',
      'By creating content, you grant the Provider a non-exclusive, royalty-free and limited licence to store, reproduce and display that content, solely for the purpose of operating the Services and only to the audience you have designated. This licence terminates when you delete the content or your account.',
      'You are solely responsible for the content you publish, including compliance with the rights of third parties.',
    ],
  },
  {
    n: 10,
    title: 'Prohibited Conduct',
    body: [
      'Harassment, threats, intimidation, or publication of obscene, discriminatory or violent material.',
      'Impersonating any natural or legal person, or misrepresenting your identity.',
      'Bulk collection, automated crawling or systematic extraction of user information by any means.',
      'Attempting unauthorised access to other accounts, to servers, or to protected parts of the Services.',
      'Commercial or promotional use, or the sending of unsolicited bulk messages.',
      'Upon establishing any of the above, the Provider may suspend or delete the account without prior notice.',
      'Zero tolerance: there is no tolerance for objectionable content or abusive users. Content that violates this section is removed and the user who posted it is ejected from the Services.',
      'Any user can report any signal, message or profile from within the application and can block any user; a blocked user\'s content is hidden from you immediately. The Provider reviews reports within 24 hours and, where a violation is established, removes the content and ejects the user.',
      'The text of signals, messages and profile names is checked against a list of objectionable words before it is published, and is not published if it matches. If you believe the check stopped your text by mistake, you can appeal from the same place and the Provider will review it.',
    ],
  },
  {
    n: 11,
    title: 'In-Person Meetings and Disclaimer',
    body: [
      'The Services are merely a tool for coordination between people who already know one another. The Provider does not screen, verify or guarantee the identity, background, suitability or good faith of any user.',
      'The decision to meet any person, the choice of place, time and circumstances, and all consequences arising therefrom, rest solely with you.',
      'The Provider accepts no liability whatsoever for injury, loss or damage of any kind occurring during or as a result of an in-person meeting between users.',
      'Users are advised to take ordinary precautions, including choosing a public place for a first meeting.',
    ],
  },
  {
    n: 12,
    title: 'Place, Event and Business Information',
    body: [
      'Information about places, events and businesses shown in the discovery section may be compiled from public sources or submitted by users.',
      'The Provider does not warrant the accuracy, currency or completeness of such information, including opening hours, prices, addresses and availability.',
      'Verifying such information directly, before acting upon it, is your responsibility.',
    ],
  },
  {
    n: 13,
    title: 'Text Messages and Contact Outside the Services',
    body: [
      'The Services send a text message only to verify a phone number, and only where you yourself initiate that verification. That message contains a single-use code and is delivered through the Twilio service (section 8). Apart from that verification, no other text message is sent by the Services.',
      'The Services have no access to the messages stored on your device.',
      'Invitation by text message is separate from the above: when you choose that option, your device\'s own messaging application is opened with prepared text, and sending it is your own direct act. Neither the Provider nor any third-party service plays a part in that path, and the message text does not pass through the servers of the Services.',
      'The cost of sending is governed by your carrier\'s tariff, in which the Provider plays no part.',
    ],
  },
  {
    n: 14,
    title: 'Notifications',
    body: [
      'The Services may send notifications regarding friends\' activity, invitations and messages.',
      'You may disable notifications at any time through the application settings or your device settings.',
    ],
  },
  {
    n: 15,
    title: 'Your Rights Regarding Your Data',
    body: [
      'Access to the data held about you.',
      'Correction of inaccurate or incomplete data.',
      'Deletion of your account and its associated data.',
      'Deletion of part of your data without deleting your account: your profile picture, phone numbers, status text, the contacts you have imported from your device, the plans you created, the events you added, and your name on the messages you sent. Each is carried out separately and immediately from the same deletion page in settings, and requires no request to the Provider.',
      'The last three were also seen by other people and are therefore bounded: a plan your friends have already answered, and an event on which someone has already built a plan, remain visible and are marked as called off, so that what others built upon them still makes sense; and the text of your messages remains within other people\'s conversations, but its link to your name and profile is severed.',
      'Withdrawal of consents granted, including access to the address book.',
      'Any of the above may be exercised through the application settings or by writing to the address in section 24.',
    ],
  },
  {
    n: 16,
    title: 'Retention and Deletion',
    body: [
      'Your data is retained for as long as your account remains active.',
      'On deletion of your account, your profile, signals, messages, groups and stored contacts are deleted.',
      'Your display name, username, email address and phone number are additionally hashed, such that they cannot be restored to their original values. This is irreversible, and a deleted account is not reinstated.',
      'Messages you sent in another user\'s signal conversation may remain in order to preserve the coherence of that conversation, but their link to your profile is severed.',
      'Backups may continue to contain data for a reasonable period after deletion and are overwritten in their ordinary cycle.',
    ],
  },
  {
    n: 17,
    title: 'Service Provided "As Is"',
    body: [
      'The Services are provided "as is" and "with all faults".',
      'The Provider makes no warranty, express or implied, as to uninterrupted operation, freedom from error, absolute security, or fitness for any particular purpose of the user.',
      'The Provider does not warrant that signals, messages or notifications will be delivered on time or at all.',
      'The Provider may modify, restrict or discontinue any part of the Services at any time without prior notice.',
    ],
  },
  {
    n: 18,
    title: 'Limitation of Liability',
    body: [
      'To the maximum extent permitted by applicable law, the Provider shall not be liable for indirect, consequential, incidental, punitive or special damages, including loss of profit, loss of data or damage to reputation.',
      'In any event, the Provider\'s aggregate liability to you shall not exceed the amount you have paid for the Services in the preceding twelve months. Where no amount has been paid, that cap is zero.',
      'Some jurisdictions do not permit the limitation of certain liabilities; in such cases the above limitations apply only to the extent permitted by law and the remaining provisions continue in force.',
    ],
  },
  {
    n: 19,
    title: 'Indemnity',
    body: [
      'You undertake to hold the Provider harmless against any claim, demand, loss or expense arising from your breach of these terms, from content you have published, or from your infringement of the rights of third parties.',
    ],
  },
  {
    n: 20,
    title: 'Suspension and Termination',
    body: [
      'You may end this contractual relationship at any time by deleting your account.',
      'The Provider may suspend or terminate your access where these terms are breached, or where continued provision would pose a risk to other users.',
      'Sections 9, 11, 18, 19 and 22 survive termination.',
    ],
  },
  {
    n: 21,
    title: 'Changes to This Document',
    body: [
      'The Provider may revise these terms at any time. The current text is always available from the application settings.',
      'Where a material change is made, you will be informed through the application before it takes effect and your renewed acceptance will be requested.',
      'Continued use of the Services after changes take effect constitutes acceptance of the new version. If you do not accept the new version, your remedy is to stop using the Services and delete your account.',
      'Each version bears its own number, and the date and version number you accepted are recorded and retained.',
    ],
  },
  {
    n: 22,
    title: 'Governing Law and Forum',
    body: [
      'These terms are governed by the laws of the Province of Ontario and the applicable federal laws of Canada.',
      'Any dispute arising from these terms shall fall within the exclusive jurisdiction of the courts situated in the Province of Ontario.',
      'Where the mandatory law of your place of residence grants you rights beyond this document, those rights are reserved.',
    ],
  },
  {
    n: 23,
    title: 'Severability and Entire Agreement',
    body: [
      'The invalidity of any provision shall not affect the validity of the remainder, and an invalid provision shall so far as possible be construed to its nearest valid meaning.',
      'This document constitutes the entire agreement between you and the Provider concerning the Services and supersedes any prior understanding.',
      'A failure by the Provider to exercise any right immediately shall not be treated as a waiver of that right.',
    ],
  },
  {
    n: 24,
    title: 'Contact',
    body: [
      `Questions, data requests and reports of breaches of these terms should be sent to: ${CONTACT_EMAIL}`,
    ],
  },
];

export const TERMS: Record<'fa' | 'en', LegalDoc> = {
  fa: {
    title: 'شرایط استفاده و حریم خصوصی',
    updated: `نسخه‌ی ${TERMS_VERSION} — ${TERMS_DATE}`,
    intro: 'این سند رابطه‌ی حقوقی میان شما و {brand} را تعیین می‌کند و نحوه‌ی '
      + 'گردآوری، استفاده و نگهداری اطلاعات شما را شرح می‌دهد. لطفاً پیش از '
      + 'پذیرش، آن را بخوانید.',
    sections: FA_SECTIONS,
    close: `{brand} — نسخه‌ی ${TERMS_VERSION} — ${TERMS_DATE}`,
  },
  en: {
    title: 'Terms of Use and Privacy Policy',
    updated: `Version ${TERMS_VERSION} — ${TERMS_DATE_EN}`,
    intro: 'This document sets out the legal relationship between you and {brand} '
      + 'and describes how your information is collected, used and retained. '
      + 'Please read it before accepting.',
    sections: EN_SECTIONS,
    close: `{brand} — version ${TERMS_VERSION} — ${TERMS_DATE_EN}`,
  },
};

/**
 * متن به زبان کاربر، و اگر آن زبان را نداریم، انگلیسی.
 *
 * عمداً به سیزده زبان ترجمه نشده: متن حقوقیِ ماشین‌ترجمه‌شده بدتر از
 * نبودنش است، چون خواننده گمان می‌کند دقیق است.
 */
/**
 * متنِ حقوقی هم `{brand}` می‌نویسد، ولی برخلافِ بقیه‌ی رشته‌ها از `t()` رد
 * نمی‌شود — این سند ساختار دارد (عنوان، بندها، زیربندها) نه یک کلیدِ ترجمه.
 * پس جایگزینی همین‌جا، سرِ خروجی، انجام می‌شود.
 *
 * چرا سرِ خروجی و نه یک بار موقعِ ساختِ ثابت‌ها: نامِ برند در زمانِ اجرا
 * تعیین می‌شود (بوتِ وب از روی دامنه)، و ثابت‌های ماژول پیش از آن ساخته شده‌اند.
 */
const fillBrand = (s: string): string => s.replace(/\{brand\}/g, brand());

export function legalDoc(locale: string): LegalDoc {
  const doc = locale === 'fa' ? TERMS.fa : TERMS.en;
  return {
    ...doc,
    title: fillBrand(doc.title),
    intro: fillBrand(doc.intro),
    close: fillBrand(doc.close),
    sections: doc.sections.map((s) => ({
      ...s,
      title: fillBrand(s.title),
      body: s.body.map(fillBrand),
    })),
  };
}

/**
 * «این آدم باید دوباره بپذیرد؟»
 *
 * بند ۲۱ سند قول داده که تغییرِ بااهمیت پیش از اجرایی شدن به اطلاع کاربر
 * می‌رسد و «پذیرش مجدد شما درخواست می‌شود». تا پیش از این، بالا بردنِ
 * `TERMS_VERSION` هیچ اثری نداشت: عدد فقط موقعِ **ساختِ** نمایه نوشته
 * می‌شد و هیچ‌کس بعداً نگاهش نمی‌کرد. یعنی قولِ بند ۲۱ قابل اجرا نبود.
 *
 * `null` (حسابی که پیش از مایگریشن 0026 ساخته شده) هم یعنی «نپذیرفته» —
 * صفر گرفتنش امن‌ترین حالت است: بدترین پیامدش یک بار پذیرشِ اضافه است، در
 * برابرِ کاربری که هیچ‌وقت چیزی نپذیرفته.
 */
export function needsTermsAcceptance(acceptedVersion: number | null | undefined): boolean {
  return (acceptedVersion ?? 0) < TERMS_VERSION;
}
